import { Button, Input, Label, ModalShell } from '@xhubio-saas/core-frontend/ui-components'
import { LoaderCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ISO_COUNTRY_CODES } from './isoCountries'

/**
 * What the dialog sends: the country, and outside Germany the company details the buyer typed — trimmed, empty
 * fields left out. The server decides whether that is a company (billing buyer rule); the dialog invents no rule.
 */
export interface BuyerCountryInput {
  countryCode: string
  vatId?: string
  legalName?: string
  taxNumber?: string
}

/** House limits (`textfeld-obergrenzen.md`): `legalName` 200, tax and VAT numbers 30. */
const MAX_LEGAL_NAME = 200
const MAX_TAX_ID = 30

/** The country whose buyers need no company details (E2: „außerhalb DE Firmenangaben"). */
const HOME_COUNTRY = 'DE'

export interface CountryDialogProps {
  /** Role owner/admin — `organization.update` refuses everyone else (E5). */
  canSet: boolean
  onCancel: () => void
  /** Sets the country. Rejects on a refusal; the dialog then stays open (the global toast speaks). */
  onConfirm: (input: BuyerCountryInput) => Promise<void>
}

function compact(input: BuyerCountryInput): BuyerCountryInput {
  const out: BuyerCountryInput = { countryCode: input.countryCode }
  for (const key of ['vatId', 'legalName', 'taxNumber'] as const) {
    const value = input[key]?.trim()
    if (value) out[key] = value
  }
  return out
}

/**
 * The country before checkout (plan API 2026-10-09/02, step 3): choose (E4: every ISO country) → confirm with the
 * country named, because it can be set only once (E3). Without owner/admin only the sentence that they have to buy
 * (E5) — no field, no call.
 */
export function CountryDialog({ canSet, onCancel, onConfirm }: CountryDialogProps) {
  const { t, i18n } = useTranslation()
  const [step, setStep] = useState<'choose' | 'confirm'>('choose')
  const [values, setValues] = useState<BuyerCountryInput>({ countryCode: '' })
  const [busy, setBusy] = useState(false)

  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.language], { type: 'region' })
    return ISO_COUNTRY_CODES.map((code) => ({ code, name: names.of(code) ?? code })).sort((a, b) =>
      a.name.localeCompare(b.name, i18n.language)
    )
  }, [i18n.language])
  const countryName = countries.find((c) => c.code === values.countryCode)?.name ?? ''
  const abroad = values.countryCode !== '' && values.countryCode !== HOME_COUNTRY

  const set = (key: keyof BuyerCountryInput, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }))

  const confirm = async () => {
    setBusy(true)
    try {
      await onConfirm(compact(abroad ? values : { countryCode: values.countryCode }))
    } catch (err) {
      // The refusal is spoken by the global error toast; the dialog stays open for a correction.
      console.error('[Billing] setting the country failed:', err)
    } finally {
      setBusy(false)
    }
  }

  if (!canSet) {
    return (
      <ModalShell
        open
        onClose={onCancel}
        title={t('billingPage.country.title')}
        testIdPrefix="billing-country"
        footer={
          <div className="flex justify-end gap-4">
            <Button onClick={onCancel} data-testid="billing-country-close">
              {t('billingPage.country.close')}
            </Button>
          </div>
        }
      >
        <p className="text-sm" data-testid="billing-country-not-allowed">
          {t('billingPage.country.notAllowed')}
        </p>
      </ModalShell>
    )
  }

  const footer =
    step === 'choose' ? (
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={onCancel} data-testid="billing-country-cancel">
          {t('billingPage.country.cancel')}
        </Button>
        <Button
          onClick={() => setStep('confirm')}
          disabled={values.countryCode === ''}
          data-testid="billing-country-next"
        >
          {t('billingPage.country.next')}
        </Button>
      </div>
    ) : (
      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={() => setStep('choose')}
          disabled={busy}
          data-testid="billing-country-back"
        >
          {t('billingPage.country.back')}
        </Button>
        <Button
          onClick={() => void confirm()}
          disabled={busy}
          data-testid="billing-country-confirm"
        >
          {busy && (
            <LoaderCircle
              className="h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          {t('billingPage.country.submit')}
        </Button>
      </div>
    )

  return (
    <ModalShell
      open
      onClose={busy ? undefined : onCancel}
      title={t('billingPage.country.title')}
      testIdPrefix="billing-country"
      footer={footer}
    >
      {step === 'choose' ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('billingPage.country.intro')}</p>
          <div className="space-y-2">
            <Label htmlFor="billing-country-select">{t('billingPage.country.label')}</Label>
            <select
              id="billing-country-select"
              value={values.countryCode}
              onChange={(e) => set('countryCode', e.target.value)}
              data-testid="billing-country-select"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="" disabled>
                {t('billingPage.country.placeholder')}
              </option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {abroad && (
            <fieldset className="space-y-3 border-t pt-4">
              <legend className="text-sm text-muted-foreground">
                {t('billingPage.country.companyHint')}
              </legend>
              <div className="space-y-2">
                <Label htmlFor="billing-country-vat-id">{t('billingPage.country.vatId')}</Label>
                <Input
                  id="billing-country-vat-id"
                  value={values.vatId ?? ''}
                  onChange={(e) => set('vatId', e.target.value)}
                  maxLength={MAX_TAX_ID}
                  autoComplete="off"
                  data-testid="billing-country-vat-id"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-country-legal-name">
                  {t('billingPage.country.legalName')}
                </Label>
                <Input
                  id="billing-country-legal-name"
                  value={values.legalName ?? ''}
                  onChange={(e) => set('legalName', e.target.value)}
                  maxLength={MAX_LEGAL_NAME}
                  autoComplete="organization"
                  data-testid="billing-country-legal-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-country-tax-number">
                  {t('billingPage.country.taxNumber')}
                </Label>
                <Input
                  id="billing-country-tax-number"
                  value={values.taxNumber ?? ''}
                  onChange={(e) => set('taxNumber', e.target.value)}
                  maxLength={MAX_TAX_ID}
                  autoComplete="off"
                  data-testid="billing-country-tax-number"
                />
              </div>
            </fieldset>
          )}
        </div>
      ) : (
        <p className="text-sm" data-testid="billing-country-confirm-text">
          {t('billingPage.country.confirm', { country: countryName })}{' '}
          <strong>{t('billingPage.country.final')}</strong>
        </p>
      )}
    </ModalShell>
  )
}
